import { AxiosResponse } from "axios";
import CandidateAction from "../../models/candidate-action.model";
import { CandidateDetail, CandidateDetailV2 } from "../../models/candidate-detail.model";
import Plan from "../../models/plan.model";
import { Candidate, CandidateSimple } from "./../../models/candidate.model";
import { CandidateQuery } from "./models/candidates-query.model";
import API from "./talent-api-config";
import APIv2 from "./talent-api-config-v2";
import qs from "qs";

class TalentApi {
  async fetchTalent(
    talentId: string,
    trackId?: string,
    ecosystem?: string,
    currencyIso?: string
  ): Promise<AxiosResponse<CandidateDetail>> {
    if (!trackId) {
      return API.get(
        `/talent/pool/user/${talentId}/?ecosystem=${ecosystem}&currency=${currencyIso}`
      );
    }

    return API.get(
      `/talent/track/${trackId}/candidate/${talentId}?currency=${currencyIso}`
    );
  }

  async fetchDeclineCandidateReasons(): Promise<AxiosResponse> {
    return API.get(`/candidate-reasons/`);
  }

  async sendAction(
    candidateAction: CandidateAction,
    talentId: string,
    trackId?: string
  ): Promise<AxiosResponse<CandidateDetail>> {
    if (!trackId) {
      return API.post(`/talent/pool/user/${talentId}/`, candidateAction);
    }
    return API.post(
      `/talent/track/${trackId}/candidate/${talentId}/`,
      candidateAction
    );
  }

  async fetchCandidatePool(
    ecosystemId: string,
    query?: CandidateQuery
  ): Promise<Candidate[]> {
    const params = {
      ...query,
    };
    const response = await API.get<Candidate[]>(
      `/talent/pool/ecosystem/${ecosystemId}/`,
      { params }
    );

    if (response.data) {
      for (let i = 0; i < response.data.length; i++) {
        const candidate = response.data[i];
        candidate.is_pool = true;
      }
    }

    return response.data;
  }

  async fetchCandidateOfRanking(
    main_skill: string,
    query?: CandidateQuery
  ): Promise<AxiosResponse<CandidateSimple[]>> {
    const params = {
      main_skill,
      ...query,
    };
    return await APIv2.get<CandidateSimple[]>(
      `/candidate/search`,
      { params: params, paramsSerializer: (params) => { return qs.stringify(params, { arrayFormat: 'repeat' }) } }
    );
  }

  async fetchCandidateDetailOfRanking(
    talentId: string,
  ): Promise<AxiosResponse<CandidateDetailV2>> {
    return await APIv2.get<CandidateDetailV2>(
      `/candidate/${talentId}`
    );
  }

  async fetchSkillsOfCandidatePool(
    ecosystemId: string,
    query?: CandidateQuery
  ): Promise<AxiosResponse<string[]>> {
    const page = query?.candidatePoolPage
      ? parseInt(query?.candidatePoolPage)
      : 1;
    const params = { page_size: 10, page };
    return await API.get<string[]>(
      `/talent/pool/ecosystem/${ecosystemId}/skills/`,
      { params }
    );
  }

  async updateUserLanguage(
    language: string,
    user_id: string
  ): Promise<AxiosResponse> {
    return await API.patch(`/users/${user_id}/preference/language/`, {
      language,
    });
  }

  async updateCompanyLanguage(
    language: string,
    company_id: string
  ): Promise<AxiosResponse> {
    return await API.patch(`/corporates/${company_id}/language/`, { language });
  }

  async updateCompanyCurrency(
    currency: string,
    company_id: string
  ): Promise<AxiosResponse> {
    return await API.patch(`/corporates/${company_id}/currency/`, { currency });
  }

  async createTag(name: string, color: string) {
    return await API.post(`/talent/tags/`, { name, color });
  }

  async removeCandidateTag(tagId: string, candidateId: string) {
    return await API.delete(`/talent/tags/${tagId}/candidate/remove/${candidateId}/`);
  }

  async addCandidateTag(tagsId: string[], candidateId: string) {
    return await API.post(`/talent/tags/candidate/add/${candidateId}/`, { tags: tagsId });
  }

  async fetchTags(name?: string) {
    return await API.get(`/talent/tags?q=${name}`);
  }

  async fetchTagsByIds(ids: string[]) {
    return await API.get(`/talent/tags`, { params: { ids: ids }, 
      paramsSerializer: (params) => { return qs.stringify(params, { arrayFormat: 'indices' }) } });
  }

  async fetchTalentPlan(): Promise<AxiosResponse<Plan>> {
    return API.get(`/talent/pool/plan/`);
  }

  async setCandidateNotAfordable(talentId: string, trackId: string, jobSetupId: string): Promise<AxiosResponse> {
    return API.post(
      `/talent/track/${trackId}/candidate/${talentId}/not-suitable/`, 
      { job_setup_id: jobSetupId });
  }
}

export const talentApi = new TalentApi();
